import { promises as fs } from 'fs';
import { join } from 'path';
import type { ProjectInspectionSignals } from '../types/project-inspector.types.js';

/**
 * Rules configuration within project config
 */
export interface RulesConfig {
    preset: string;
    version: string;
    appliedAt: string;
    ides: string[];
    customContext: string;
    backups?: Array<{
        path: string;
        timestamp: string;
        ides: string[];
    }>;
}

export interface CoreV3ProjectState {
    schemaVersion: 1;
    primaryPackage: string;
    baselineFingerprint: string;
    baselineSignals?: ProjectInspectionSignals;
    bootstrappedAt: string;
    lastReviewedAt?: string;
    metadata?: Record<string, string>;
}

/**
 * Project-level Jai1 configuration
 * Stored in .jai1/project.json
 */
export interface Jai1ProjectConfig {
    rules?: RulesConfig;
    coreV3?: CoreV3ProjectState;
    // Future extensions:
    // kit?: { ... }
    // mcp?: { ... }
}

/**
 * Service for managing project-level Jai1 configuration
 * Config stored in .jai1/project.json (relative to project root)
 */
export class ProjectConfigService {
    private readonly projectRoot: string;
    private readonly configDir: string;
    private readonly configPath: string;

    constructor(projectRoot: string = process.cwd()) {
        this.projectRoot = projectRoot;
        this.configDir = join(this.projectRoot, '.jai1');
        this.configPath = join(this.configDir, 'project.json');
    }

    /**
     * Check if config file exists
     */
    async exists(): Promise<boolean> {
        try {
            await fs.access(this.configPath);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Load full project configuration
     * @returns Config object or null if not found
     */
    async load(): Promise<Jai1ProjectConfig | null> {
        if (!(await this.exists())) {
            return null;
        }

        try {
            const content = await fs.readFile(this.configPath, 'utf-8');
            return JSON.parse(content) as Jai1ProjectConfig;
        } catch (error) {
            throw new Error(
                `Failed to load project config: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    /**
     * Save full project configuration
     * Creates .jai1 directory if it doesn't exist
     */
    async save(config: Jai1ProjectConfig): Promise<void> {
        try {
            await fs.mkdir(this.configDir, { recursive: true });
            await fs.writeFile(this.configPath, JSON.stringify(config, null, 2), 'utf-8');
        } catch (error) {
            throw new Error(
                `Failed to save project config: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    /**
     * Load rules configuration only
     * @returns RulesConfig or null if not found
     */
    async loadRules(): Promise<RulesConfig | null> {
        const config = await this.load();
        return config?.rules ?? null;
    }

    /**
     * Save rules configuration
     * Merges with existing config, preserving other sections
     */
    async saveRules(rulesConfig: RulesConfig): Promise<void> {
        const existingConfig = (await this.load()) ?? {};
        existingConfig.rules = rulesConfig;
        await this.save(existingConfig);
    }

    async loadCoreV3(): Promise<CoreV3ProjectState | null> {
        const config = await this.load();
        return config?.coreV3 ?? null;
    }

    async saveCoreV3(coreV3State: CoreV3ProjectState): Promise<void> {
        const existingConfig = (await this.load()) ?? {};
        existingConfig.coreV3 = coreV3State;
        await this.save(existingConfig);
    }

    /**
     * Update rules configuration partially
     * Merges with existing rules config
     */
    async updateRules(partialRulesConfig: Partial<RulesConfig>): Promise<void> {
        const existingRules = await this.loadRules();
        if (!existingRules) {
            throw new Error('No rules configuration found. Run "jai1 rules apply" first.');
        }

        const updatedRules: RulesConfig = {
            ...existingRules,
            ...partialRulesConfig,
        };
        await this.saveRules(updatedRules);
    }

    /**
     * Add a backup entry to rules config
     * Keeps only the last 5 backups
     */
    async addBackup(backup: { path: string; timestamp: string; ides: string[] }): Promise<void> {
        const rules = await this.loadRules();
        if (!rules) {
            throw new Error('No rules configuration found.');
        }

        const backups = [backup, ...(rules.backups ?? [])].slice(0, 5);
        await this.updateRules({ backups });
    }

    /**
     * Get config file path
     */
    getConfigPath(): string {
        return this.configPath;
    }

    /**
     * Get config directory path (.jai1/)
     */
    getConfigDir(): string {
        return this.configDir;
    }

    /**
     * Get project root path
     */
    getProjectRoot(): string {
        return this.projectRoot;
    }
}
